<template>
  <div class="container-fluid py-4">
    <div class="d-flex justify-content-between align-items-center mb-4 header-clinique flex-wrap gap-2">
      <div class="d-flex align-items-center gap-3">
        <i class="bi bi-people fs-2 text-primary"></i>
        <div>
          <h2 class="fw-bold mb-0">Patients</h2>
          <small class="text-muted">{{ total.toLocaleString('fr-FR') }} dossier(s)</small>
        </div>
      </div>
      <button v-if="$store.getters.hasPermission('PATIENT_CREATE')" class="btn btn-primary shadow-sm btn-clinique" @click="creation">
        <i class="bi bi-plus-circle me-1"></i>Nouveau patient
      </button>
    </div>

    <div class="card p-3 mb-3 shadow-sm rounded card-clinique">
      <div class="row g-2 align-items-end">
        <div class="col-12 col-md-5">
          <label class="form-label fw-semibold small mb-1">Rechercher</label>
          <input v-model="recherche" type="search" class="form-control form-control-clinique" placeholder="Nom, prénom, téléphone, n° de dossier…">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">Enregistré du</label>
          <input v-model="du" type="date" class="form-control form-control-clinique">
        </div>
        <div class="col-6 col-md-2">
          <label class="form-label fw-semibold small mb-1">au</label>
          <input v-model="au" type="date" class="form-control form-control-clinique">
        </div>
        <div class="col-12 col-md-3">
          <label class="form-label fw-semibold small mb-1">Trier par</label>
          <select v-model="tri" class="form-select form-select-clinique">
            <option value="pat.createdAt">Date d'enregistrement</option>
            <option value="pat.nom">Nom</option>
            <option value="pat.prenom">Prénom</option>
            <option value="pat.numeroDossier">N° de dossier</option>
          </select>
        </div>
      </div>
    </div>

    <div v-if="erreur" class="alert alert-danger d-flex justify-content-between align-items-center">
      <span>{{ erreur }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="charger">Réessayer</button>
    </div>

    <div class="card shadow-sm rounded card-clinique">
      <div class="table-responsive" :class="{ 'opacity-50': chargement }">
        <table class="table align-middle mb-0 table-hover modern-table-clinique w-100">
          <thead>
            <tr>
              <th>#</th>
              <th>Enregistré le</th>
              <th>N° dossier</th>
              <th>Patient</th>
              <th class="d-none d-sm-table-cell">Téléphone</th>
              <th class="d-none d-md-table-cell text-center">Consultations</th>
              <th class="d-none d-lg-table-cell">Enregistré par</th>
              <th class="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(p, i) in lignes" :key="p.id">
              <td class="text-muted">{{ rang(i) }}</td>
              <td>{{ formatDate(p.createdAt) }}</td>
              <td>{{ p.numeroDossier || '—' }}</td>
              <td>
                <a v-if="$store.getters.hasPermission('PATIENT_DOSSIER')" href="#" class="fw-semibold" @click.prevent="dossier(p.id)">{{ p.prenom }} {{ p.nom }}</a>
                <span v-else class="fw-semibold">{{ p.prenom }} {{ p.nom }}</span>
                <div v-if="p.adresse" class="small text-muted">{{ p.adresse }}</div>
              </td>
              <td class="d-none d-sm-table-cell">{{ p.telephone || '—' }}</td>
              <td class="d-none d-md-table-cell text-center">{{ p.nombreConsultations ?? 0 }} <small class="text-muted">({{ p.nombreConsultationsTerminees ?? 0 }} terminée(s))</small></td>
              <td class="d-none d-lg-table-cell small">{{ p.createdByPrenom }} {{ p.createdByNom }}</td>
              <td class="text-end">
                <div class="dropdown">
                  <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" v-dropdown-fixed>Actions</button>
                  <ul class="dropdown-menu">
                    <li v-if="$store.getters.hasPermission('PATIENT_DOSSIER')"><a class="dropdown-item" href="#" @click.prevent="dossier(p.id)">Dossier</a></li>
                    <li v-if="$store.getters.hasPermission('PATIENT_EDIT')"><a class="dropdown-item" href="#" @click.prevent="modifier(p.id)">Modifier</a></li>
                    <li v-if="$store.getters.hasPermission('PATIENT_DELETE')"><a class="dropdown-item text-danger" href="#" @click.prevent="supprimer(p)">Supprimer</a></li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="!chargement && !lignes.length">
              <td colspan="8" class="text-center text-muted py-4">{{ recherche || du || au ? 'Aucun patient ne correspond à la recherche.' : 'Aucun patient enregistré.' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="px-3 pb-3">
        <Pagination v-model:page="page" v-model:par-page="parPage" :total="total" />
      </div>
    </div>
  </div>
</template>

<script>
import api, { messageErreur, notifier } from '@/api';
import dayjs from 'dayjs';
import Pagination from '@/components/commun/Pagination.vue';
import { listeServeur } from '@/components/commun/listeServeur';
import { confirmer } from '@/components/commun/dialogue';

export default {
  name: 'ListePatient',
  components: { Pagination },
  mixins: [listeServeur('patients', { tri: 'pat.createdAt', sens: 'desc' })],
  data() {
    return { du: '', au: '' };
  },
  watch: {
    du() { this.rechargerListe(); },
    au() { this.rechargerListe(); },
  },
  methods: {
    filtresListe() {
      return { du: this.du, au: this.au };
    },
    formatDate(date) {
      return date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '—';
    },
    creation() {
      this.$router.push({ name: 'create_patient' });
    },
    dossier(id) {
      this.$router.push({ name: 'information_personnelle', params: { id } });
    },
    modifier(id) {
      this.$router.push({ name: 'edit_patient', params: { id } });
    },
    async supprimer(p) {
      const ok = await confirmer({
        titre: 'Supprimer le dossier',
        message: `Supprimer le dossier de ${p.prenom} ${p.nom} ? Seul un dossier vide (créé par erreur) peut être supprimé.`,
        libelleValider: 'Supprimer',
        danger: true,
      });
      if (!ok) return;
      try {
        await api.deleteItem('patients', p.id);
        notifier('Dossier supprimé.', 'success');
        this.charger();
      } catch (e) {
        notifier(messageErreur(e, 'Suppression impossible.'), 'danger');
      }
    },
  },
};
</script>
